import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { approveCartAPI } from 'src/api';
import { ToastStyled } from 'src/components';
import { useCurrentUser } from 'src/hooks';
import {
  CartApprovalDetails,
  CartApprovalStatus,
  CartGetData,
} from 'src/types';

type Props = {
  cartId: string;
};

export const useApproveCart = ({ cartId }: Props) => {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      layer,
      emailOriginalRequester,
    }: {
      layer: number;
      emailOriginalRequester: boolean;
    }) => approveCartAPI(cartId, layer, emailOriginalRequester),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['cart', 'list'],
        exact: false,
        type: 'all',
        refetchType: 'active',
      });

      try {
        queryClient.setQueryData(
          ['cart', cartId],
          (oldData: CartGetData): CartGetData => {
            const oldApprovalDetails = oldData.approvalDetails;

            if (!oldApprovalDetails) {
              // error out and invalidate the query
              throw new Error();
            }

            const oldApprovalDetail = oldApprovalDetails.at(vars.layer - 1);

            const newApprovalDetail = {
              ...oldApprovalDetail,
              actionedBy: {
                id: user?.id || '',
                email: user?.email || '',
                name: user?.name || '',
              },
              actionedAt: Date.now(),
              status: 'approved' as CartApprovalStatus,
            };

            // Update only the specific approval detail at the current layer, keep all others unchanged
            const newApprovalDetails = oldApprovalDetails.map(
              (detail, index) =>
                index === vars.layer - 1 ? newApprovalDetail : detail,
            );

            return {
              ...oldData,
              approvalDetails: newApprovalDetails as CartApprovalDetails,
            };
          },
        );
      } catch (_) {
        queryClient.invalidateQueries({
          queryKey: ['cart', cartId],
          type: 'all',
        });
      }
    },
    onError: (error) => {
      toast.custom((toastBlock) => (
        <ToastStyled
          variant='error'
          handleClose={() => toast.dismiss(toastBlock)}
        >
          Failed to approve cart. Please try again.
        </ToastStyled>
      ));
    },
  });
};
